import { pageBgColor } from "@/utils/theme";
import {
	ErrorModal,
	HelmetDexteel,
	useHasPermission,
	useMesfRealtime,
} from "@dexteel/mesf-core";
import { Box, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import moment from "moment-timezone";
import { useWeeklyIncentive } from "./hooks/useWeeklyIncentive";
import { WeekPickerHeader } from "./components/WeekPickerHeader";
import { DailySummaryGrid } from "./components/DailySummaryGrid";
import { CrewIncentivesPivot } from "./components/CrewIncentivesPivot";
import { IncentiveTanksPanel } from "./components/IncentiveTanksPanel";
import { EditShiftOverrideModal } from "./components/EditShiftOverrideModal";
import type { IncentiveRow } from "./models/IncentiveRow";

const getInitialSaturday = (): moment.Moment => {
	const now = moment();
	// Snap to the Saturday ending the current week (Sun-Sat)
	const day = now.day(); // 0=Sun, 6=Sat
	if (day === 6) return now.startOf("day");
	return now.day(6).startOf("day");
};

export const IncentivePage = () => {
	const queryClient = useQueryClient();
	const [error, setError] = useState("");
	const [weekEndingDate, setWeekEndingDate] =
		useState<moment.Moment>(getInitialSaturday);
	const [editRow, setEditRow] = useState<IncentiveRow | null>(null);
	const hasPermissionTo = useHasPermission();
	const canEdit = hasPermissionTo("ui.incentive.edit");

	const {
		data,
		isLoading,
		error: queryError,
	} = useWeeklyIncentive(weekEndingDate);

	useMesfRealtime({
		onReceiveMessage: async (_author, message) => {
			if (
				message.replaceAll("[", "").replaceAll("]", "") ===
				"M2.GetIncentiveByWeek"
			) {
				await queryClient.invalidateQueries({ queryKey: ["incentive"] });
			}
		},
	});

	useEffect(() => {
		if (queryError) setError(queryError.message);
	}, [queryError]);

	const handleWeekChange = (newValue: moment.Moment | null) => {
		if (!newValue) return;
		// Snap to Saturday
		const snapped = newValue.clone().day(6).startOf("day");
		setWeekEndingDate(snapped);
	};

	return (
		<>
			<HelmetDexteel title="Incentive Dashboard" />
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "calc(100vh - 64px)",
					backgroundColor: pageBgColor,
					p: "10px 20px",
					overflow: "hidden",
				}}
			>
				<Box sx={{ mb: "10px" }}>
					<WeekPickerHeader
						weekEndingDate={weekEndingDate}
						onChange={handleWeekChange}
					/>
				</Box>

				<Box
					sx={{
						display: "flex",
						gap: 1,
						flex: 1,
						minHeight: 0,
						alignItems: "stretch",
						position: "relative",
					}}
				>
					{isLoading && (
						<Box
							sx={{
								position: "absolute",
								inset: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: "rgba(255,255,255,0.6)",
								zIndex: 10,
							}}
						>
							<CircularProgress />
						</Box>
					)}
					{/* Left column: Daily Summary */}
					<Box sx={{ flex: 2, minWidth: 0 }}>
						<DailySummaryGrid
							dailyRows={data?.dailyRows ?? []}
							dayTotals={data?.dayTotals ?? []}
							canEdit={canEdit}
							onEdit={setEditRow}
						/>
					</Box>

					{/* Right column: Crew Pivot + Tanks */}
					<Box
						sx={{
							flex: 1,
							minWidth: 0,
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						<CrewIncentivesPivot crewPivot={data?.crewPivot ?? []} />
						<IncentiveTanksPanel tanks={data?.tanks ?? []} />
					</Box>
				</Box>
			</Box>
			{canEdit && (
				<EditShiftOverrideModal row={editRow} onHide={() => setEditRow(null)} />
			)}
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
